/**
 * Raw Axios wrappers for Groups (and related feed) endpoints.
 */
import type { AxiosResponse } from 'axios';
import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';
import GROUPS_ENDPOINTS from './groupsEndpoints';
import { withFeedTimezoneOffset } from '../feed/feedRequestPayload';
import type {
  AddMemberRequest,
  ApiActionResponse,
  ApiGetFeedsResponse,
  ApiGetGroupDetailResponse,
  ApiGetGroupsListResponse,
  ApiGroupMutationEnvelope,
  ApiJoinRequestsEnvelope,
  ApiMyGroupItem,
  ApiPaginatedGroups,
  ChangeMemberRoleRequest,
  CreateGroupRequest,
  GetFeedsRequest,
  GetGroupJoinRequestsPayload,
  GetGroupsListRequest,
  GetGroupsQueryParams,
  GroupIdRequest,
  RemoveMemberRequest,
  ReviewJoinRequestPayload,
  UpdateGroupRequest,
} from './groupsTypes';

const groupsApi = {
  createGroup: (
    payload: FormData | CreateGroupRequest,
  ): Promise<AxiosResponse<ApiGroupMutationEnvelope>> =>
    apiClient.post(GROUPS_ENDPOINTS.CREATE_GROUP, payload, {
      headers: payload instanceof FormData
        ? { 'Content-Type': 'multipart/form-data' }
        : undefined,
    }),

  updateGroup: (
    payload: FormData | UpdateGroupRequest,
  ): Promise<AxiosResponse<ApiGroupMutationEnvelope>> =>
    apiClient.put(GROUPS_ENDPOINTS.UPDATE_GROUP, payload, {
      headers: payload instanceof FormData
        ? { 'Content-Type': 'multipart/form-data' }
        : undefined,
    }),

  joinGroup: (
    payload: GroupIdRequest,
  ): Promise<AxiosResponse<ApiActionResponse>> =>
    apiClient.post(GROUPS_ENDPOINTS.JOIN_GROUP, payload),

  cancelJoinRequest: (
    payload: GroupIdRequest,
  ): Promise<AxiosResponse<ApiActionResponse>> =>
    apiClient.post(GROUPS_ENDPOINTS.CANCEL_JOIN_REQUEST, payload),

  leaveGroup: (
    payload: GroupIdRequest,
  ): Promise<AxiosResponse<ApiActionResponse>> =>
    apiClient.post(GROUPS_ENDPOINTS.LEAVE_GROUP, payload),

  deleteGroup: (
    payload: GroupIdRequest,
  ): Promise<AxiosResponse<ApiActionResponse>> =>
    apiClient.post(GROUPS_ENDPOINTS.DELETE_GROUP, payload),

  addMember: (payload: AddMemberRequest): Promise<AxiosResponse<void>> =>
    apiClient.post(GROUPS_ENDPOINTS.ADD_MEMBER, payload),

  changeMemberRole: (
    payload: ChangeMemberRoleRequest,
  ): Promise<AxiosResponse<void>> =>
    apiClient.post(GROUPS_ENDPOINTS.CHANGE_MEMBER_ROLE, payload),

  removeMember: (
    payload: RemoveMemberRequest,
  ): Promise<AxiosResponse<void>> =>
    apiClient.post(GROUPS_ENDPOINTS.REMOVE_MEMBER, payload),

  reviewJoinRequest: (
    payload: ReviewJoinRequestPayload,
  ): Promise<AxiosResponse<ApiActionResponse>> =>
    apiClient.post(GROUPS_ENDPOINTS.REVIEW_JOIN_REQUEST, payload),

  getGroups: (
    params: GetGroupsQueryParams,
  ): Promise<AxiosResponse<ApiPaginatedGroups>> =>
    apiClient.get(GROUPS_ENDPOINTS.GET_GROUPS, { params }),

  listMyGroups: (
    params: GetGroupsQueryParams,
  ): Promise<AxiosResponse<ApiMyGroupItem[]>> =>
    apiClient.get(GROUPS_ENDPOINTS.LIST_MY_GROUPS, { params }),

  getGroupsList: (
    payload: GetGroupsListRequest,
  ): Promise<AxiosResponse<ApiGetGroupsListResponse>> =>
    apiClient.post(GROUPS_ENDPOINTS.GET_GROUPS_LIST, payload),

  getGroupDetail: (
    payload: GroupIdRequest,
  ): Promise<AxiosResponse<ApiGetGroupDetailResponse>> =>
    apiClient.post(
      GROUPS_ENDPOINTS.GET_GROUP_DETAIL,
      withFeedTimezoneOffset(payload),
    ),

  getGroupJoinRequests: (
    payload: GetGroupJoinRequestsPayload,
  ): Promise<AxiosResponse<ApiJoinRequestsEnvelope>> =>
    apiClient.post(GROUPS_ENDPOINTS.GET_GROUP_JOIN_REQUESTS, payload),

  getFeeds: (
    payload: GetFeedsRequest,
  ): Promise<AxiosResponse<ApiGetFeedsResponse>> =>
    apiClient.post(
      API_ENDPOINTS.FEEDS.GET_FEEDS,
      withFeedTimezoneOffset(payload),
    ),

  postReaction: (payload: {
    postId: number;
    reactionTypeId: number;
  }): Promise<AxiosResponse<ApiActionResponse>> =>
    apiClient.post(API_ENDPOINTS.FEEDS.POST_REACTION, payload),
};

export default groupsApi;
